import React, { useState, useRef, useCallback } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  FileUploadOutlined,
  InfoOutlined,
  DescriptionOutlined,
  CheckCircleOutline,
  WarningAmber,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { baseAxios } from "../../utils/config";
import { toast } from "react-hot-toast";
import { buildImportFormData, importHasIssues } from "./importHelpers";

// Stable defaults so optional props do not churn hook dependencies.
const NO_FIELDS = {};
const NO_ITEMS = [];

/**
 * Generic multi-step CSV import dialog: upload -> dry-run preview -> import
 * -> results. Works against any endpoint built on the API's shared CSV import
 * runner. Entity-specific wording, columns and extras come in as props; see
 * ImportClientsDialog and ImportServicesDialog for usage.
 *
 * @param {string} entityPlural   lowercase plural for messages, e.g. "clients"
 * @param {string} entityTitle    title-case plural for titles/buttons, e.g. "Clients"
 * @param {string} subtitle       line under the dialog title
 * @param {string} importPath     e.g. "/clients/import"; template is `${importPath}/template`
 * @param {string} templateFilename  download name for the template
 * @param {string[]} requiredColumns  shown as chips in the upload step
 * @param {string} optionalColumnsHint  text under the chips
 * @param {string[]} formatGuide  bullet lines in the collapsible format guide
 * @param {{key: string, label: string, render?: (row: object) => React.ReactNode}[]} previewColumns
 * @param {string} existingIdKey  key of the existing-record id on skipped rows
 * @param {{key: string, label: string}[]} extraResultCounts  shown on results when > 0
 * @param {Object<string,string>} extraFields  appended to the FormData of both calls
 * @param {(ctx: {dryRunResult: object, revalidate: Function, loading: boolean}) => React.ReactNode} renderPreviewExtras
 * @param {(ctx: {importResult: object}) => React.ReactNode} renderAfterResults  optional extra step after
 *   a successful import (e.g. attaching product images). Reached from a button on the results screen.
 * @param {string} afterResultsLabel  label of that button and title of the step, e.g. "Add images"
 * @param {(validRows: number) => string} importButtonLabel  overrides "Import N <Entity>"
 * @param {(importResult: object) => string} resultsSuccessMessage  overrides "N <entity> imported successfully!"
 * @param {(ctx: {importResult: object}) => React.ReactNode} renderResultsExtras  extra detail on the
 *   results screen, under the counts (e.g. rows that failed while importing)
 */
export default function CsvImportDialog({
  open,
  onClose,
  onImportComplete,
  entityPlural,
  entityTitle,
  subtitle,
  importPath,
  templateFilename,
  requiredColumns = NO_ITEMS,
  optionalColumnsHint = "",
  formatGuide = NO_ITEMS,
  previewColumns = NO_ITEMS,
  existingIdKey,
  extraResultCounts = NO_ITEMS,
  extraFields = NO_FIELDS,
  renderPreviewExtras,
  renderAfterResults,
  afterResultsLabel = "Continue",
  importButtonLabel,
  resultsSuccessMessage,
  renderResultsExtras,
}) {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [formatGuideOpen, setFormatGuideOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setStep("upload");
      setFile(null);
      setDryRunResult(null);
      setImportResult(null);
      setLoading(false);
      setError(null);
      setActiveTab(0);
      setDetailsExpanded(false);
    }
  }, [open]);

  // --- API functions ---

  const downloadTemplate = useCallback(async () => {
    try {
      const res = await baseAxios.get(`${importPath}/template`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = templateFilename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  }, [importPath, templateFilename]);

  // Runs the dry run with the given extra fields. Resolves true on success.
  const runDryRun = useCallback(
    async (fields) => {
      if (!file) return false;
      setLoading(true);
      setError(null);
      try {
        const res = await baseAxios.post(
          `${importPath}?dry_run=true`,
          buildImportFormData(file, fields),
          { headers: { "Content-Type": undefined } }
        );
        setDryRunResult(res.data);
        return true;
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.data?.message ||
            "Validation failed"
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [file, importPath]
  );

  const handleValidate = useCallback(async () => {
    const ok = await runDryRun(extraFields);
    if (ok) {
      setActiveTab(0);
      setStep("preview");
    }
  }, [runDryRun, extraFields]);

  // Re-run the dry run from the preview step, e.g. after an entity-specific
  // choice changed. Pass the next extra fields explicitly: the parent's state
  // update has not reached this component's props yet when this is called.
  const revalidate = useCallback(
    (nextExtraFields) => runDryRun(nextExtraFields ?? extraFields),
    [runDryRun, extraFields]
  );

  const handleImport = useCallback(async () => {
    if (!file) return;
    setStep("importing");
    try {
      const res = await baseAxios.post(
        importPath,
        buildImportFormData(file, extraFields),
        { headers: { "Content-Type": undefined } }
      );
      setImportResult(res.data);
      setStep("results");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.data?.message ||
          "Import failed"
      );
      setStep("preview");
      toast.error("Import failed");
    }
  }, [file, importPath, extraFields]);

  // --- File handling ---

  const validateFile = (f) => {
    if (!f.name.toLowerCase().endsWith(".csv"))
      return "Please select a CSV file";
    if (f.size > 5 * 1024 * 1024) return "File exceeds 5MB limit";
    return null;
  };

  const handleFileSelect = (f) => {
    const err = validateFile(f);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileSelect(f);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  // --- Close handler ---

  const handleClose = () => {
    if (step === "importing") return;
    if (
      (step === "results" || step === "after") &&
      importResult?.inserted > 0
    ) {
      onImportComplete?.();
      toast.success(importResult.message);
    }
    onClose();
  };

  // --- Render ---

  const isImporting = step === "importing";
  const dialogTitle =
    {
      upload: `Import ${entityTitle}`,
      preview: `Import ${entityTitle} - Preview`,
      importing: `Import ${entityTitle}`,
      results: "Import Complete",
      after: afterResultsLabel,
    }[step] || `Import ${entityTitle}`;
  const canContinueAfterResults =
    typeof renderAfterResults === "function" && importResult?.inserted > 0;

  return (
    <Dialog
      open={open}
      onClose={isImporting ? undefined : handleClose}
      maxWidth={step === "preview" || step === "after" ? "md" : "sm"}
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          bgcolor: "secondary.main",
          color: "common.white",
          borderBottom: "1px solid",
          borderColor: "primary.main",
          px: 3,
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="inherit">
            {dialogTitle}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
          >
            {subtitle}
          </Typography>
        </Box>
        {!isImporting && (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
            sx={{
              color: "primary.main",
              mt: 0.25,
              "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {/* ===== UPLOAD STEP ===== */}
        {step === "upload" && (
          <>
            {!file ? (
              <Box
                onClick={() => fileInputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                sx={{
                  border: "2px dashed",
                  borderColor: isDragging ? "primary.main" : "#ccc",
                  borderRadius: 2,
                  p: 5,
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragging
                    ? "action.hover"
                    : "transparent",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <FileUploadOutlined
                  sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                />
                <Typography variant="body1" fontWeight={500}>
                  Drag & drop a CSV file here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  or click to browse
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Accepted: .csv (max 5MB, 5,000 rows)
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileSelect(f);
                    e.target.value = "";
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <DescriptionOutlined color="primary" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setFile(null);
                    setError(null);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            <Alert
              severity="info"
              variant="outlined"
              icon={<InfoOutlined fontSize="small" />}
              sx={{ mt: 2, mb: 1 }}
            >
              <AlertTitle
                sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 0.5 }}
              >
                Column Requirements
              </AlertTitle>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexWrap: "wrap",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Required:
                </Typography>
                {requiredColumns.map((column) => (
                  <Chip
                    key={column}
                    label={column}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {optionalColumnsHint}
              </Typography>
            </Alert>

            <Button
              size="small"
              onClick={() => setFormatGuideOpen((p) => !p)}
              endIcon={
                formatGuideOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />
              }
              sx={{ mb: 0.5 }}
            >
              {formatGuideOpen ? "Hide format guide" : "View format guide"}
            </Button>
            <Collapse in={formatGuideOpen}>
              <Box sx={{ pl: 1, mb: 1 }}>
                {formatGuide.map((line) => (
                  <Typography key={line} variant="body2" color="text.secondary">
                    • {line}
                  </Typography>
                ))}
              </Box>
            </Collapse>

            <Box sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
              >
                Download Template (.csv)
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Pre-filled with all column headers and example data
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}

        {/* ===== PREVIEW STEP ===== */}
        {step === "preview" && dryRunResult && (
          <>
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <Typography
                variant="body2"
                sx={{ color: "success.main", fontWeight: 600 }}
              >
                &#10003; {dryRunResult.valid_rows} valid
              </Typography>
              {dryRunResult.error_count > 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: "error.main", fontWeight: 600 }}
                >
                  &#10005; {dryRunResult.error_count} errors
                </Typography>
              )}
              {dryRunResult.skipped > 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: "warning.main", fontWeight: 600 }}
                >
                  &#8856; {dryRunResult.skipped} skipped
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Total: {dryRunResult.total_rows} rows
              </Typography>
            </Box>

            {renderPreviewExtras?.({ dryRunResult, revalidate, loading })}

            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
              >
                <Tab label={`Valid (${dryRunResult.valid_rows})`} />
                <Tab label={`Errors (${dryRunResult.error_count})`} />
                <Tab label={`Skipped (${dryRunResult.skipped})`} />
              </Tabs>
            </Box>

            {/* Tab: Valid Rows */}
            {activeTab === 0 && (
              <TableContainer sx={{ maxHeight: 300, mt: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      {previewColumns.map((column) => (
                        <TableCell key={column.key}>{column.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!dryRunResult.valid_data ||
                    dryRunResult.valid_data.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={previewColumns.length + 1} align="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 2 }}
                          >
                            {dryRunResult.valid_rows === 0
                              ? "No valid rows"
                              : `${dryRunResult.valid_rows} rows ready for import`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {dryRunResult.valid_data.map((vr) => (
                          <TableRow key={vr.row}>
                            <TableCell>{vr.row}</TableCell>
                            {previewColumns.map((column) => (
                              <TableCell key={column.key}>
                                {column.render
                                  ? column.render(vr)
                                  : vr[column.key] || "\u2014"}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                        {dryRunResult.valid_rows >
                          dryRunResult.valid_data.length && (
                          <TableRow>
                            <TableCell colSpan={previewColumns.length + 1} align="center">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Showing {dryRunResult.valid_data.length} of{" "}
                                {dryRunResult.valid_rows} valid rows
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab: Errors */}
            {activeTab === 1 && (
              <TableContainer sx={{ maxHeight: 300, mt: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dryRunResult.row_errors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 2 }}
                          >
                            No errors
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      dryRunResult.row_errors.flatMap((rowErr) =>
                        rowErr.field_errors.map((fe, j) => (
                          <TableRow key={`${rowErr.row}-${j}`}>
                            <TableCell>{rowErr.row}</TableCell>
                            <TableCell>{fe.field}</TableCell>
                            <TableCell
                              sx={{
                                maxWidth: 120,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {fe.value || "\u2014"}
                            </TableCell>
                            <TableCell>{fe.message}</TableCell>
                          </TableRow>
                        ))
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab: Skipped */}
            {activeTab === 2 && (
              <TableContainer sx={{ maxHeight: 300, mt: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Existing ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dryRunResult.skipped_rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 2 }}
                          >
                            No skipped rows
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      dryRunResult.skipped_rows.map((sr) => (
                        <TableRow key={sr.row}>
                          <TableCell>{sr.row}</TableCell>
                          <TableCell>{sr.reason}</TableCell>
                          <TableCell>
                            {sr[existingIdKey] ?? "\u2014"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {(dryRunResult.error_count > 0 || dryRunResult.skipped > 0) && (
              <Alert severity="info" sx={{ mt: 2, fontSize: "0.85rem" }}>
                {dryRunResult.error_count > 0 &&
                  `${dryRunResult.error_count} row(s) with errors will not be imported. `}
                {dryRunResult.skipped > 0 &&
                  `${dryRunResult.skipped} duplicate row(s) will be skipped.`}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}

        {/* ===== IMPORTING STEP ===== */}
        {step === "importing" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" fontWeight={500}>
              Importing {dryRunResult?.valid_rows ?? 0} {entityPlural}...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please do not close this window.
            </Typography>
          </Box>
        )}

        {/* ===== RESULTS STEP ===== */}
        {step === "results" && importResult && (
          <>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              {!importHasIssues(importResult) ? (
                <CheckCircleOutline
                  sx={{ fontSize: 48, color: "success.main", mb: 1 }}
                />
              ) : (
                <WarningAmber
                  sx={{ fontSize: 48, color: "warning.main", mb: 1 }}
                />
              )}
              <Typography variant="h6" fontWeight={600}>
                {importHasIssues(importResult)
                  ? "Import completed with issues"
                  : resultsSuccessMessage
                    ? resultsSuccessMessage(importResult)
                    : `${importResult.inserted} ${entityPlural} imported successfully!`}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 0.5,
                maxWidth: 300,
                mx: "auto",
                mb: 2,
              }}
            >
              <Typography variant="body2">Inserted:</Typography>
              <Typography variant="body2" fontWeight={600}>
                {importResult.inserted}
              </Typography>
              {extraResultCounts.map(
                (count) =>
                  importResult[count.key] > 0 && (
                    <React.Fragment key={count.key}>
                      <Typography variant="body2">{count.label}:</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {importResult[count.key]}
                      </Typography>
                    </React.Fragment>
                  )
              )}
              {importResult.skipped > 0 && (
                <>
                  <Typography variant="body2">
                    Skipped (duplicates):
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {importResult.skipped}
                  </Typography>
                </>
              )}
              {importResult.error_count > 0 && (
                <>
                  <Typography variant="body2">Errors:</Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="error"
                  >
                    {importResult.error_count}
                  </Typography>
                </>
              )}
            </Box>

            {renderResultsExtras?.({ importResult })}

            {(importResult.row_errors.length > 0 ||
              importResult.skipped_rows.length > 0) && (
              <>
                <Button
                  size="small"
                  onClick={() => setDetailsExpanded((p) => !p)}
                  endIcon={
                    detailsExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )
                  }
                  sx={{ mb: 1 }}
                >
                  {detailsExpanded ? "Hide details" : "View details"}
                </Button>
                <Collapse in={detailsExpanded}>
                  {importResult.row_errors.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Errors:
                      </Typography>
                      {importResult.row_errors.slice(0, 20).map((re) => (
                        <Typography
                          key={re.row}
                          variant="caption"
                          display="block"
                          color="error"
                        >
                          Row {re.row}:{" "}
                          {re.field_errors
                            .map(
                              (fe) =>
                                `${fe.field} "${fe.value}" \u2014 ${fe.message}`
                            )
                            .join("; ")}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {importResult.skipped_rows.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Skipped:
                      </Typography>
                      {importResult.skipped_rows
                        .slice(0, 20)
                        .map((sr) => (
                          <Typography
                            key={sr.row}
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            Row {sr.row}: {sr.reason}
                          </Typography>
                        ))}
                    </Box>
                  )}
                </Collapse>
              </>
            )}
          </>
        )}

        {/* ===== AFTER-RESULTS STEP (entity-specific, optional) ===== */}
        {step === "after" &&
          importResult &&
          renderAfterResults?.({ importResult })}
      </DialogContent>

      {/* ===== DIALOG ACTIONS ===== */}
      {step === "upload" && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            gap: 1,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            color="secondary"
            sx={{ textTransform: "none", minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleValidate}
            disabled={!file || loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {loading ? "Validating..." : "Validate File"}
          </Button>
        </DialogActions>
      )}

      {step === "preview" && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            gap: 1,
          }}
        >
          <Button
            onClick={() => {
              setStep("upload");
              setError(null);
            }}
            startIcon={<ArrowBackIcon />}
            color="secondary"
            sx={{ mr: "auto", textTransform: "none" }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={!dryRunResult?.valid_rows || loading}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {importButtonLabel
              ? importButtonLabel(dryRunResult?.valid_rows ?? 0)
              : `Import ${dryRunResult?.valid_rows ?? 0} ${entityTitle}`}
          </Button>
        </DialogActions>
      )}

      {step === "results" && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            gap: 1,
          }}
        >
          <Button
            variant={canContinueAfterResults ? "outlined" : "contained"}
            color={canContinueAfterResults ? "secondary" : "primary"}
            onClick={handleClose}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Close
          </Button>
          {canContinueAfterResults && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setStep("after")}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {afterResultsLabel}
            </Button>
          )}
        </DialogActions>
      )}

      {step === "after" && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleClose}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Done
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
